import {PageTemplate} from "../../common/page-template/PageTemplate";
import {useTranslation} from "react-i18next";
import {
    AppConfigurationData,
    usePostApiV1AppConfigurationMutation,
    usePutApiV1AppConfigurationMutation
} from "../../../api/GovernanceToolApi";
import {AppConfigurationKeys} from "../../../book/AppConfigurationKeys";
import {Button, TextField} from "@mui/material";
import React, {useEffect, useState} from "react";
import SaveIcon from '@mui/icons-material/Save';
import {TableSkeleton} from "../../common/skeleton/TableSkeleton";
import {ErrorMessage} from "../../common/error-message/ErrorMessage";
import {
    ProcessStatusNotification,
    toProcessStatus
} from "../../common/process-status-notification/ProcessStatusNotification";

type ValueValidator = (value: string | null | undefined) => boolean;
type ConfigurationHelper = { [configuration: string]: { helperText: string, valueValidator: ValueValidator } };

export const Configuration = () => {
    const {t} = useTranslation();
    const [searchConfigurations, {
        isLoading: isConfigurationsLoading,
        error: configurationsError,
    }] = usePostApiV1AppConfigurationMutation();
    const [updateConfigurations, {
        data: updateConfigurationsData,
        isLoading: isUpdateConfigurationsLoading,
        error: updateConfigurationsError,
        reset: updateConfigurationsReset
    }] = usePutApiV1AppConfigurationMutation();
    const [state, setState] = useState<AppConfigurationData[]>([]);

    useEffect(() => {
        if (!state.length && !isConfigurationsLoading) {
            searchConfigurations({searchAppConfigurationsRequest: {}})
                .then(result => {
                    if ('data' in result) {
                        setState(result.data.payload?.configurations ?? []);
                    }
                });
        }
    });

    const configurationsHelpers: ConfigurationHelper = {};
    configurationsHelpers[AppConfigurationKeys.AllowedEmailDomain] = {
        helperText: t("Use comma as separator, example: carel.com,partner.carel.com"),
        valueValidator: (value) => {
            if (value) {
                return value.split(',').reduce((agg, curr) => {
                    return agg && /^([a-z]+[a-z0-9]*\.)+[a-z]{2,3}$/.test(curr);
                }, true as boolean);
            }

            return false;
        }
    };

    const isFormValid = state.reduce((agg, curr) =>
        agg && configurationsHelpers.hasOwnProperty(curr.id) ? configurationsHelpers[curr.id].valueValidator(curr.value) : true, true);

    return <PageTemplate pageTitle={t("Configurations")}>
        <form noValidate onSubmit={() => {
            if (isFormValid) {
                updateConfigurations({
                    updateAppConfigurationRequest: {
                        configurations: state
                    }
                }).then(() => updateConfigurationsReset());
            }

            return false;
        }}>

            {isConfigurationsLoading && <TableSkeleton rowCount={1} colCount={1}/>}

            {Boolean(configurationsError) && <ErrorMessage
                message={t("An error occurred while loading configurations, please contact the administrator or try again")}/>}

            {state.map(configuration => {
                const isValid = configurationsHelpers.hasOwnProperty(configuration.id) ? configurationsHelpers[configuration.id].valueValidator(configuration.value) : true;

                return <TextField key={configuration.id}
                                  sx={{
                                      marginBottom: '20px'
                                  }}
                                  variant="outlined"
                                  label={t(configuration.id)}
                                  fullWidth
                                  disabled={isUpdateConfigurationsLoading}
                                  error={!isValid}
                                  value={configuration.value ?? ''}
                                  helperText={configurationsHelpers.hasOwnProperty(configuration.id) ? configurationsHelpers[configuration.id].helperText : undefined}
                                  onChange={e => {
                                      const newConfigurationValue = e.target.value;

                                      setState(state => {
                                          const newState = [...state];
                                          const valueIndex = newState.findIndex(c => c.id === configuration.id);
                                          const newValue = {...newState[valueIndex]};

                                          newValue.value = newConfigurationValue;
                                          newState[valueIndex] = newValue;

                                          return newState;
                                      });
                                  }}/>;
            })}

            <Button
                disabled={!isFormValid || isUpdateConfigurationsLoading}
                type="submit"
                variant="contained"
                color="secondary"
                endIcon={<SaveIcon/>}>
                {t("Save")}
            </Button>
        </form>

        <ProcessStatusNotification
            status={toProcessStatus(isUpdateConfigurationsLoading, Boolean(updateConfigurationsData?.payload), Boolean(updateConfigurationsError))}
            onClose={() => updateConfigurationsReset()}
            onLoadingMessage={t("Updating configurations please wait…")}
            onSuccessMessage={t("Configurations updated successfully")}
            onErrorMessage={t("Configurations updating failed")}/>
    </PageTemplate>
}